import recordStore from "@/store/record.store";
import { IPG } from "@/types/summary";
import { findLastConsecutiveNumber, isEqualContent, splitPara } from "./utils";
import { isEqual } from "lodash";
import { v4 as uuidV4 } from "uuid";
import {
  resetNewSpeakerAvatarMap,
  setNewSpeakerAvatar,
} from "@/utils/newAvatar";
import { maxRecordRoleId } from "@/constants/record";
import { IBlurParaInfo } from "@/types/record";

class RecordHelper {
  private static signSenWithSpeakerName(newLastRecord: IPG, item: IPG) {
    if (!newLastRecord.sc) return;

    const itemLastSendIndex = newLastRecord.sc.findIndex(
      (lastRecordSItem) => lastRecordSItem.si === item.sc[item.sc.length - 1].si
    );
    // 短的句子后台不会发106过来，这时把它归为与下一句发言人一致的
    newLastRecord.sc.forEach((_item, index) => {
      if (newLastRecord.sc && index <= itemLastSendIndex) {
        newLastRecord.sc[index].withSpeakerName = true;
      }
    });
  }

  public static addRecord(data: { texts: IPG[]; status: string }) {
    try {
      const { texts, status } = data;
      if (status === "unstable") {
        const str = texts.reduce(
          (pre, cur) =>
            pre + cur.sc.reduce((p, c) => p + (c.tc ? c.tc : ""), ""),
          ""
        );
        if (!str) return;
        recordStore.getState().setTmpRecordInfo({ unstableText: str });
      } else if (status === "stable") {
        const { lastRecord } = recordStore.getState().getTmpRecordInfo();
        let newLastRecord = { ...lastRecord };
        texts.forEach((item) => {
          if (newLastRecord) {
            // 去除空句子，标点符号和前一个词的bt和et相同，这里将标点符号和前一个词合并为一词
            const formatSc = item.sc
              .filter((sItem) => {
                return sItem.words.length > 0;
              })
              .map((sItem) => {
                sItem.words = sItem.words.filter((wItem, index) => {
                  const preWItem = sItem.words[index - 1];
                  if (
                    preWItem &&
                    wItem.bt === preWItem.bt &&
                    wItem.et === preWItem.et
                  ) {
                    preWItem.word = preWItem.word + wItem.word;
                    return false;
                  }
                  return true;
                });
                return sItem;
              });
            newLastRecord = {
              ...item,
              // 添加稳态句子
              sc: newLastRecord.sc?.concat(formatSc),
            };
          } else {
            newLastRecord = item;
          }
        });
        recordStore.getState().setTmpRecordInfo({
          lastRecord: newLastRecord as IPG,
          stableText: "",
          unstableText: "",
        });
      } else if (status === "completeWithSpeakerName") {
        const { lastRecord } = recordStore.getState().getTmpRecordInfo();
        const records = recordStore.getState().getRecords();
        let newLastRecord = { ...lastRecord };
        const newRecords = JSON.parse(JSON.stringify(records)) as IPG[];
        let exitNoSpeakerNameRecord = false;
        texts.forEach((item) => {
          // 设置发言人信息
          if (!newLastRecord.speakerName || !newLastRecord.speakerName.length) {
            const itemUseDefaultIcon = !item.headImage;
            const itemSpeakerName =
              item.speakerName && item.speakerName[0] === "未知发言人"
                ? ["发言人"]
                : item.speakerName;
            const { avatar, role } = setNewSpeakerAvatar(
              itemSpeakerName[0],
              item.role
            );
            const itemHeadImage = item.headImage || avatar;
            const itemRole = item.role || role;
            newLastRecord.speakerName = itemSpeakerName;
            newLastRecord.headImage = itemHeadImage;
            newLastRecord.useDefaultIcon = itemUseDefaultIcon;
            newLastRecord.role = itemRole;
            // 检查下records中是否存在由拆分段导致的还没有发言人的
            newRecords.forEach((recordItem) => {
              if (!recordItem.speakerName || !recordItem.speakerName.length) {
                recordItem.speakerName = itemSpeakerName;
                recordItem.headImage = itemHeadImage;
                recordItem.useDefaultIcon = itemUseDefaultIcon;
                recordItem.role = itemRole;
                exitNoSpeakerNameRecord = true;
              }
            });
            // 发言人弹窗新增该发言人
            recordStore.getState().unshiftRecentSpeakers([
              {
                speakerName: newLastRecord.speakerName,
                headImage: newLastRecord.headImage,
                useDefaultIcon: newLastRecord.useDefaultIcon,
                role: newLastRecord.role || "",
              },
            ]);
          }

          // 标识稳态中带有发言人的句子
          RecordHelper.signSenWithSpeakerName(newLastRecord as IPG, item);
          const withoutSpeakerSc = newLastRecord.sc?.filter(
            (sItem) => !sItem.withSpeakerName
          );
          newLastRecord.sc = newLastRecord.sc?.filter(
            (sItem) => sItem.withSpeakerName
          );
          // 判断带有发言人的句子不为空，设置pi 并分段（当106没有找到105的si时，就会导致sc是空的, 此时将该条消息丢弃，否则会导致时序紊乱）
          if (newLastRecord.sc && newLastRecord.sc.length > 0) {
            newLastRecord.pi = item.pi;
            newRecords.push(newLastRecord as IPG);
          }
          // 更新稳态中剩余还未标识发言人的句子
          newLastRecord = {
            ...lastRecord,
            sc: withoutSpeakerSc,
          };
        });
        // 更新稳态视觉
        recordStore.getState().setTmpRecordInfo({
          lastRecord: newLastRecord as IPG,
          stableText: "",
          unstableText: "",
        });
        // 更新已分段的段落视觉
        if (newRecords.length !== records.length || exitNoSpeakerNameRecord) {
          recordStore.getState().setRecords(newRecords);
        }
      } else if (status === "correct") {
        this.correctRecordOnMessage(texts);
      }
    } catch (error) {
      console.log("addRecord error", error);
    }
  }

  public static mergeRecord(pi: string, isLastRecord = false) {
    // console.log("mergeRecord", pi);
    const records = recordStore.getState().getRecords();
    const newRecords = JSON.parse(JSON.stringify(records)) as IPG[];

    // 操作的record为stableRecord，那就删除最后一条records，并插入到lastRecord中
    if (isLastRecord) {
      const { lastRecord: targetRecord } = recordStore
        .getState()
        .getTmpRecordInfo();
      const lastRecord = newRecords.pop();

      if (!targetRecord || !lastRecord) return;
      const flatWords = targetRecord.sc?.flatMap((scItem) => scItem.words);
      recordStore.getState().updateOperatedInfo(
        flatWords.length
          ? {
              wi: flatWords[0].wi,
            }
          : {
              pi: lastRecord.pi,
            }
      );
      recordStore.getState().setRecords([...newRecords]);

      const newLastRecord = {
        ...targetRecord,
      };
      newLastRecord.sc = lastRecord.sc.concat(targetRecord.sc);

      recordStore.getState().setTmpRecordInfo({
        lastRecord: newLastRecord as IPG,
      });
    }
    // 操作的record为records中的, 就删除操作的records, 并合并到上一条中
    else {
      const recordIndex = newRecords.findIndex((item) => item.pi === pi);
      const lastRecord = newRecords[recordIndex - 1];
      const targetRecord = newRecords[recordIndex];
      if (!targetRecord) return;
      if (!lastRecord) {
        const filteredNewRecords = newRecords.filter((item) => item.pi !== pi);
        recordStore.getState().setRecords(filteredNewRecords);
        return 0;
      }
      const flatWords = targetRecord.sc?.flatMap((scItem) => scItem.words);
      recordStore.getState().updateOperatedInfo(
        flatWords.length
          ? {
              wi: flatWords[0].wi,
            }
          : {
              pi: lastRecord.pi,
            }
      );
      lastRecord.sc = lastRecord.sc.concat(targetRecord.sc);

      const filteredNewRecords = newRecords.filter((item) => item.pi !== pi);
      recordStore.getState().setRecords(filteredNewRecords);

      return recordIndex - 1;
    }
  }

  public static splitRecord(
    pi: string,
    wi: string,
    cIndex: number,
    isLastRecord = false
  ) {
    // console.log("splitRecord", wi, cIndex);
    const { lastRecord } = recordStore.getState().getTmpRecordInfo();
    const records = recordStore.getState().getRecords();
    const newRecords = JSON.parse(JSON.stringify(records)) as IPG[];
    const newLastRecord = JSON.parse(JSON.stringify(lastRecord)) as IPG;
    if (isLastRecord && newLastRecord) {
      let si;
      newLastRecord.sc.forEach((sItem) => {
        sItem.words.forEach((wItem) => {
          if (wItem.wi === wi) {
            si = sItem.si;
          }
        });
      });
      if (!si) return;
      const [currentIPG, newIPG] = splitPara(newLastRecord, wi, si, cIndex);
      recordStore.getState().setTmpRecordInfo({ lastRecord: { ...newIPG } });
      newRecords.push(currentIPG);
      recordStore.getState().unshiftRecentSpeakers([
        {
          speakerName: newLastRecord.speakerName,
          headImage: newLastRecord.headImage,
          useDefaultIcon: newLastRecord.useDefaultIcon,
          role: newLastRecord.role,
        },
      ]);
      recordStore.getState().setRecords([...newRecords]);
      const flatWords = newIPG.sc?.flatMap((scItem) => scItem.words);

      recordStore.getState().updateOperatedInfo(
        newIPG.sc[0].words.length
          ? {
              wi: flatWords.find((item) => !!item.word)?.wi, // 这里第一个字段会被删除
            }
          : {
              pi: newIPG.pi,
            }
      );
      return;
    }
    const wordMap = recordStore.getState().getWordMap();
    const si = wordMap[wi]?.parent?.id;
    const targetPara = newRecords.find((item) => item.pi === pi);
    const targetParaIndex = newRecords.findIndex((item) => item.pi === pi);
    if (!targetPara) return;
    const [currentIPG, newIPG] = splitPara(targetPara, wi, si, cIndex);
    newRecords.splice(targetParaIndex, 1, ...[currentIPG, newIPG]);
    recordStore.getState().setRecords([...newRecords]);

    recordStore.getState().updateOperatedInfo(
      newIPG.sc[0].words.length
        ? {
            wi: newIPG.sc[0].words[0].wi,
          }
        : {
            pi: newIPG.pi,
          }
    );
    return targetParaIndex;
  }

  private static addSent(targetPara: IPG, wi: string, newContent: string) {
    const { bt, et } = targetPara;
    if (bt === undefined || et === undefined) return;
    console.log("handle addSent", bt, et, newContent);
    targetPara.sc = [
      {
        bt,
        et,
        tc: newContent,
        transTc: "",
        id: Date.now(),
        mark: false,
        realPara: {...targetPara, sc: []},
        si: `s-${uuidV4()}`,
        words: [
          {
            word: newContent,
            bt,
            et,
            wi,
            mark: false,
          },
        ],
      },
    ];
  }

  public static editRecord(
    pi: string,
    wg: {
      wi: string;
      newContent: string;
    }[],
    options?: { isLastRecord: boolean; emptyWordAllowed: boolean }
  ) {
    const { isLastRecord = false, emptyWordAllowed = false } = options || {};
    // console.log("editRecord", pi, wg);
    const { lastRecord } = recordStore.getState().getTmpRecordInfo();
    const newLastRecord = JSON.parse(JSON.stringify(lastRecord)) as IPG;
    if (isLastRecord && newLastRecord) {
      wg.forEach((w) => {
        const { wi, newContent } = w;
        newLastRecord.sc.forEach((sItem) => {
          sItem.words = sItem.words.filter((wItem) => {
            if (wItem.wi === wi) {
              wItem.word = newContent;
              return !!newContent;
            }
            return true;
          });
        });
      });
      recordStore.getState().setTmpRecordInfo({ lastRecord: newLastRecord });
      return;
    }
    const records = recordStore.getState().getRecords();
    const newRecords = JSON.parse(JSON.stringify(records)) as IPG[];
    const wordMap = recordStore.getState().getWordMap();
    console.log('editRecord', wg);
    wg.forEach((w) => {
      const { wi, newContent } = w;
      const targetPara = newRecords.find((item) => item.pi === pi);

      if (wi &&  !wordMap[wi] && targetPara && newContent) {
        // 新增句子
        RecordHelper.addSent(targetPara, wi, newContent);
        return;
      }
      const si = wordMap[wi]?.parent.id;
      const targetSent = targetPara?.sc.find((item) => item.si === si);
      const targetWord = targetSent?.words.find((item) => item.wi === wi);
      if (targetWord) {
        targetWord.word = newContent;
      }
      if (targetSent && !emptyWordAllowed && targetSent?.words.length > 1) {
        targetSent.words = targetSent?.words.filter((item) => !!item.word);
        // 词为空的句子当有多句时删除其余的空句
        if (
          targetPara &&
          targetPara?.sc?.length > 1 &&
          targetSent.words.length === 0
        ) {
          targetPara.sc = targetPara.sc.filter((item) => item.words.length);
        }
      }
    });
    recordStore.getState().setRecords(newRecords);
  }

  public static signRecord(pi: string, signFlag = true, isLastRecord = false) {
    const { lastRecord } = recordStore.getState().getTmpRecordInfo();
    if (isLastRecord && lastRecord) {
      lastRecord.sc.forEach((sItem) => {
        sItem.words.forEach((word) => {
          word.mark = signFlag;
        });
      });
      recordStore
        .getState()
        .setTmpRecordInfo({ lastRecord: { ...lastRecord } });
      return;
    }
    const records = recordStore.getState().getRecords();
    const newRecords = records.map((item) => {
      if (item.pi === pi) {
        item.sc.forEach((sItem) => {
          sItem.words.forEach((word) => {
            word.mark = signFlag;
          });
        });
      }
      return { ...item };
    });
    recordStore.getState().setRecords([...newRecords]);
  }

  public static generateRandomNewRole() {
    // 由于后台生成发言人总结的role只能为数字类型的字符串，并且不能与现有声纹发言人的role重复，因此开启录制时获取当前声纹列表最大roleId, 保证不与我们自己新建的role相同(不同场会议不同发言人role相同roleId不影响)
    // 获取现有最大role
    const maxRoleId = recordStore.getState().getMaxRoleId();
    const randomNewRole = maxRoleId - 1;
    recordStore.getState().setMaxRoleId(randomNewRole);
    return randomNewRole.toString();
  }

  public static modifySpeakerName(
    pi: string,
    newSpeakerName: string[],
    isLastRecord = false
  ) {
    // 判断是否为新增的，还是已存在的
    const recentSpeakers = recordStore.getState().getRecentSpeakers();
    const target = recentSpeakers.find((item) =>
      isEqual(item.speakerName, newSpeakerName)
    );
    const useDefaultIcon = target ? target.useDefaultIcon : true;
    let headImage = "";
    let myRole = target?.role;
    if (target && target.headImage) {
      headImage = target.headImage;
    } else {
      const { avatar, role } = setNewSpeakerAvatar(newSpeakerName[0]);
      headImage = avatar;
      myRole = role;
    }
    recordStore.getState().unshiftRecentSpeakers([
      {
        speakerName: newSpeakerName,
        headImage,
        useDefaultIcon,
        role: myRole || "",
      },
    ]);
    const { lastRecord } = recordStore.getState().getTmpRecordInfo();
    if (isLastRecord && lastRecord) {
      lastRecord.speakerName = newSpeakerName;
      lastRecord.headImage = headImage;
      lastRecord.useDefaultIcon = useDefaultIcon;
      lastRecord.role = myRole || "";
      recordStore
        .getState()
        .setTmpRecordInfo({ lastRecord: { ...lastRecord } });
      return;
    }
    const records = recordStore.getState().getRecords();
    const newRecords = records.map((item) => {
      if (item.pi === pi) {
        item.speakerName = newSpeakerName;
        item.headImage = headImage;
        item.useDefaultIcon = useDefaultIcon;
        item.role = myRole || "";
      }
      return { ...item };
    });
    recordStore.getState().setRecords([...newRecords]);
  }

  public static deleteSpeakerName(speakerName: string[]) {
    recordStore.getState().deleteRecentSpeakers(speakerName);
  }

  public static resetAvatarMap() {
    resetNewSpeakerAvatarMap();
  }

  public static modifyAllSameSpeakerName(
    oldSpeakerName: string[],
    newSpeakerName: string[]
  ) {
    console.log("modifyAllSameSpeakerName", oldSpeakerName, newSpeakerName);
    // 判断是否为新增的，还是已存在的
    const recentSpeakers = recordStore.getState().getRecentSpeakers();
    const target = recentSpeakers.find((item) =>
      isEqual(item.speakerName, newSpeakerName)
    );
    const useDefaultIcon = target ? target.useDefaultIcon : true;
    let headImage = "";
    let myRole = target?.role;
    if (target && target.headImage) {
      headImage = target.headImage;
    } else {
      const { avatar, role } = setNewSpeakerAvatar(newSpeakerName[0]);
      headImage = avatar;
      myRole = role;
    }
    recordStore.getState().unshiftRecentSpeakers([
      {
        speakerName: newSpeakerName,
        headImage,
        useDefaultIcon,
        role: myRole || "",
      },
    ]);
    const records = recordStore.getState().getRecords();
    const newRecords = records.map((item) => {
      if (isEqual(item.speakerName, oldSpeakerName)) {
        item.speakerName = newSpeakerName;
        item.headImage = headImage;
        item.useDefaultIcon = useDefaultIcon;
        item.role = myRole || "";
      }
      return { ...item };
    });
    recordStore.getState().setRecords([...newRecords]);
  }

  public static setRecentSpeakersFromDetail(detail: IPG[]) {
    detail.forEach((item) => {
      const { speakerName, headImage, useDefaultIcon, role } = item;
      recordStore.getState().unshiftRecentSpeakers([
        {
          speakerName,
          headImage,
          useDefaultIcon,
          role,
        },
      ]);
    });
    const roleArr = detail.map((obj) => Number(obj.role));
    const currentMaxRoleId = findLastConsecutiveNumber(
      roleArr,
      maxRecordRoleId
    );
    recordStore.getState().setMaxRoleId(currentMaxRoleId);
  }

  // 收到消息校正原文
  public static correctRecordOnMessage(texts: IPG[]) {
    const focusedPara = recordStore.getState().getFocusedPara();
    const editedPiArr = recordStore.getState().getEditedPiArr();

    texts.forEach((correctItem) => {
      // 元素没有经过修改,才会进入校正
      if (editedPiArr.includes(correctItem.pi)) {
        return;
      }
      // 元素聚焦，等待失焦后取出校正
      if (correctItem.pi === focusedPara?.pi) {
        const waitingCorrectRecords = recordStore
          .getState()
          .getWaitingCorrectRecords();
        recordStore
          .getState()
          .setWaitingCorrectRecords([...waitingCorrectRecords, correctItem]);
        return;
      }

      // 元素没有经过修改，且没有聚焦，直接校正
      this._handleCorrectRecord(correctItem);
    });
  }

  private static _handleCorrectRecord(correctItem: IPG) {
    const records = recordStore.getState().getRecords();
    const newRecords = records.map((item) => {
      if (item.pi === correctItem.pi) {
        item.sc = correctItem.sc;
      }
      return item;
    });

    const waitingCorrectRecords = recordStore
      .getState()
      .getWaitingCorrectRecords();
    const editedPiArr = recordStore.getState().getEditedPiArr();
    // 过滤掉已编辑和当前已更新
    const newWaitingCorrectRecords = waitingCorrectRecords.filter(
      (item) => !editedPiArr.includes(item.pi) && item.pi !== correctItem.pi
    );

    recordStore
      .getState()
      .setWaitingCorrectRecords([...newWaitingCorrectRecords]);
    recordStore.getState().setRecords(newRecords);
  }

  // 失焦校正原文
  public static correctRecordOnBlur(blurPara: IBlurParaInfo) {
    const blurPi = blurPara.pi;
    const focusedPara = recordStore.getState().getFocusedPara();
    if (!focusedPara) return;
    const hasEdited = !isEqualContent(focusedPara, blurPara.pContent);
    const editedPiArr = recordStore.getState().getEditedPiArr();
    recordStore.getState().setFocusedPara(null);

    // 只要有一次编辑过，就会永远存在编辑列表中
    if (editedPiArr.includes(blurPi)) {
      return;
    }
    // 此次失焦编辑过，且未添加到已编辑列表中, 此时添加到编辑列表
    if (hasEdited) {
      recordStore.getState().addEditedPiArr(blurPi);
      return;
    }
    // 从未编辑过，且还未校正, 此时校正数据
    const waitingCorrectRecords = recordStore
      .getState()
      .getWaitingCorrectRecords();
    const correctItem = waitingCorrectRecords.find(
      (item) => item.pi === blurPi
    );
    if (!correctItem) return;
    this._handleCorrectRecord(correctItem);
  }
}

export default RecordHelper;
